import { createAjv } from "@jsonforms/core";
import Ajv from "ajv";
import ajvKeywords from "ajv-keywords";
import { jwtDecode } from "jwt-decode";
import _ from "lodash";
import moment from "moment-timezone";

// Utility function to remove \n, \r, \t from a string
export const removeNewLine = (str) => str.replace(/(\r\n|\n|\r|\t|\\"|\\\\)/gm, " ");

// Utility function to remove specific characters from a string
export const removeChar = (str, char, replace = "") =>
  str.replace(new RegExp(char, "g"), replace);

// Utility function to replace characters in a string
export const replaceChar = (str, char, replace) => str.replace(char, replace);

// Function to validate if a string matches a regular expression
export const regExValidator = (regEx, string) => regEx.test(string);

// Convert an array of strings into a comma-separated string
export const commaSpaceSeparatedString = (array) =>
  Array.isArray(array) ? array.join(", ") : "";

export const getUploadedCodeGroupFileDetails = (group) => {
  const rawFileName = group?.fileDetails?.[0]?.name;
  const fileNameParts = rawFileName?.split("/") || [];

  if (fileNameParts.length > 1) {
    return {
      partnerId: fileNameParts[0],
      fileName: fileNameParts[1],
    };
  }

  const exportFileName = Array.isArray(group?.exportFile)
    ? group?.exportFile?.[0]
    : group?.exportFile;

  return {
    partnerId: "",
    fileName: rawFileName || exportFileName || "",
  };
};

// Check if two objects are deeply equal
export const isDeepEqual = (object1, object2) => {
  // File/Blob instances have no enumerable own keys, so a structural
  // comparison would incorrectly treat any two files (or a File and {})
  // as equal.  Compare them by reference instead.
  if (
    object1 instanceof File ||
    object1 instanceof Blob ||
    object2 instanceof File ||
    object2 instanceof Blob
  ) {
    return object1 === object2;
  }

  const objKeys1 = Object.keys(object1);
  const objKeys2 = Object.keys(object2);

  if (objKeys1.length !== objKeys2.length) return false;

  return objKeys1.every((key) => {
    const value1 = object1[key];
    const value2 = object2[key];
    const bothObjects = isObject(value1) && isObject(value2);

    return bothObjects ? isDeepEqual(value1, value2) : value1 === value2;
  });
};

// Check if a value is an object
export const isObject = (value) => value !== null && typeof value === "object";

// Function to parse YouTube video ID from a URL
export const youtubeParser = (url) => {
  const shortsRegex = /^https:\/\/www\.youtube\.com\/shorts\/([^?]+)(?:[?]|$)/;
  let match = url.match(shortsRegex);

  if (match) return match[1];

  const regExp =
    /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
  match = url.match(regExp);

  return match && match[7]?.length === 11 ? match[7] : false;
};

// Function to safely parse a JSON string
export const getJsonIfValid = (stringifiedJson = "") => {
  try {
    return JSON.parse(stringifiedJson);
  } catch (e) {
    console.warn(e);
    return false;
  }
};

// format segment attributes
export const formatSegmentKeys = (key) => {
  return key.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
};

// Convert the casing of a string based on provided parameters
export const convertKeyCasing = (key, splitBy, joinBy, casing = "UPPER") => {
  if (!key) return "";
  const formattedKey = key.split(splitBy).join(joinBy);
  return casing === "UPPER"
    ? formattedKey.toUpperCase()
    : formattedKey.toLowerCase();
};

// Validate JSON schema with data
const isValueInvalid = (value) =>
  value === "" || value === null || value === undefined;

const isInvalidInteger = (property, value) => {
  return (
    property.type === "integer" &&
    property.minimum &&
    (value < property.minimum || !Number.isInteger(value))
  );
};

const isInvalidString = (property, value) => {
  if (property.type !== "string") return false;
  if (property.minLength && value.length < property.minLength) return true;
  if (property.maxLength && value.length > property.maxLength) return true;
  return false;
};

const isInvalidObject = (property, value) => {
  return property.type === "object" && !validateSchemawithData(property, value);
};

const isInvalidUriString = (value) => {
  const ajvSchema = {
    type: "object",
    properties: {
      url: {
        type: "string",
        format: "uri",
      },
    },
    required: ["url"],
  };

  const ajv = createAjv();
  const result = ajv.validate(ajvSchema, { url: value });
  return !result;
};

const isInvalidArray = (property, value) => {
  if (property.type !== "array") return false; // Return false if not an array
  if (!Array.isArray(value) || value.length < property.minItems) return true; // Invalid array case
  if (property.items?.type === "object") {
    return !value.every((item) => validateSchemawithData(property.items, item)); // Negate the result to match invalid case
  }
  return !value.every((item) => {
    if (isValueInvalid(item)) {
      return false;
    }
    if (property.format === "uri") {
      if (isInvalidUriString(item)) {
        return false;
      }
    }
    return true;
  });

  // return !value.every((item) => !isValueInvalid(item)); // Return false if any item is invalid
};

const validatePropertyType = (property, value) => {
  if (isInvalidInteger(property, value)) return false;
  if (isInvalidString(property, value)) return false;
  if (isInvalidObject(property, value)) return false;
  if (isInvalidArray(property, value)) return false;

  return true;
};

export const validateSchemawithData = (schema, data) => {
  if (schema.type !== "object") return true;

  return (schema.required || []).every((key) => {
    const value = data[key];
    const property = schema.properties[key];

    if (!Object.prototype.hasOwnProperty.call(data, key) || isValueInvalid(value)) {
      return false;
    }

    return validatePropertyType(property, value);
  });
};

const buildAjvInstance = () => {
  const ajv = new Ajv();
  ajvKeywords(ajv, ["uniqueItemProperties"]);
  return ajv;
};

const traverseSchemaStep = (current, part) => {
  if (current == null) return null;
  if (/^\d+$/.test(part)) return current?.items;
  return current?.properties?.[part];
};

const getFieldTitle = (schema, instancePath, fieldName) => {
  const parts = [
    ...(instancePath
      ? instancePath.replace(/^\//, "").split("/").filter(Boolean)
      : []),
    ...(fieldName ? [fieldName] : []),
  ];
  const resolved = parts.reduce(
    (acc, part) => traverseSchemaStep(acc, part),
    schema
  );
  return resolved?.title;
};

const formatAjvError = (error, schema) => {
  if (error.keyword === "required") {
    const fieldName = error.params.missingProperty;
    const title = getFieldTitle(schema, error.instancePath, fieldName);
    return `"${title || fieldName}" is required`;
  }
  const rawField = error.instancePath ? error.instancePath.replace(/^\//, "") : "";
  const title = rawField ? getFieldTitle(schema, rawField, null) : null;
  const field = title || rawField;
  if (error.keyword === "not") {
    return field ? `"${field}": value is not allowed` : "value is not allowed";
  }
  if (error.keyword === "uniqueItemProperties") {
    const fieldSchema = rawField
      ? rawField.split("/").filter(Boolean).reduce(traverseSchemaStep, schema)
      : schema;
    const uniqueProps = fieldSchema?.uniqueItemProperties || [];
    const keyList = uniqueProps.map((k) => `"${k}"`).join(", ");
    return field
      ? `"${field}" must have unique ${keyList} values`
      : `must have unique ${keyList} values`;
  }
  return field ? `"${field}": ${error.message}` : error.message;
};

// Validate JSON schema using AJV
export const validateSchemaWithAJV = (schema, data) => {
  try {
    const ajv = buildAjvInstance();
    const validate = ajv.compile(schema);
    return validate(data);
  } catch (error) {
    console.error("AJV validation error:", error);
    return false;
  }
};

export const getSchemaAJVErrors = (schema, data) => {
  try {
    const ajv = buildAjvInstance();
    const validate = ajv.compile(schema);
    const isValid = validate(data);
    const errors = isValid
      ? []
      : (validate.errors || []).map((e) => formatAjvError(e, schema));
    return { isValid, errors };
  } catch (error) {
    console.error("AJV validation error:", error);
    return { isValid: false, errors: [] };
  }
};

// Decode Okta access token stored in localStorage
export const decodeOktaAccessToken = () => {
  try {
    const oktaTokenStorage = localStorage.getItem("okta-token-storage");
    if (!oktaTokenStorage) return null;

    const token = JSON.parse(oktaTokenStorage).accessToken?.accessToken || "";
    return token ? jwtDecode(token) : null;
  } catch (e) {
    console.warn(e);
    return null;
  }
};

/* returns the updated UIschema based on target scope */
export const updateUiSchema = (schema, targetScopes) => {
  if (Array.isArray(schema.elements)) {
    schema.elements = schema.elements.map((element) =>
      updateUiSchema(element, targetScopes)
    );
  }

  if (Object.prototype.hasOwnProperty.call(targetScopes, schema.scope)) {
    return { ...schema, ...targetScopes[schema.scope] };
  }

  return schema;
};

export const getStoredProgramDateFormat = () =>
  localStorage.getItem("programDateFormat") || "DD/MM/YYYY";

export const getStoredProgramDateTimeFormat = () =>
  localStorage.getItem("programDateTimeFormat") || "DD/MM/YYYY hh:mm A";

export const ensureDateTimeFormatHasSeconds = (dateTimeFormat) => {
  if (!dateTimeFormat) return dateTimeFormat;

  let inLiteral = false;
  let minuteTokenEnd = -1;
  let minuteTokenStart = -1;

  for (let i = 0; i < dateTimeFormat.length; i += 1) {
    const char = dateTimeFormat[i];

    if (char === "[") {
      inLiteral = true;
      continue;
    }

    if (char === "]") {
      inLiteral = false;
      continue;
    }

    if (inLiteral) continue;

    if (char === "s") return dateTimeFormat;

    if (char === "m") {
      minuteTokenStart = i;
      while (dateTimeFormat[i + 1] === "m") i += 1;
      minuteTokenEnd = i + 1;
    }
  }

  if (minuteTokenEnd === -1) return dateTimeFormat;

  const minuteSeparator = dateTimeFormat[minuteTokenStart - 1];
  const secondsSeparator =
    minuteSeparator && /[^A-Za-z0-9[\]]/.test(minuteSeparator)
      ? minuteSeparator
      : ":";

  return `${dateTimeFormat.slice(
    0,
    minuteTokenEnd
  )}${secondsSeparator}ss${dateTimeFormat.slice(minuteTokenEnd)}`;
};

/*
 * @deprecated - Use `convertDateToLocaleTz` instead for better timezone handling
 */
export const getDateWithFormat = (data, currentProgramTimezone) => {
  const timezone = currentProgramTimezone || "America/Chicago";
  return moment
    .tz(new Date(data), timezone)
    .format(getStoredProgramDateTimeFormat());
};

export const prettyColumnName = (key) => {
  // Replace underscores with space, e.g. outcome_id -> outcome id
  const noUnderscores = key.replaceAll("_", " ");
  // Insert a space before capital letters, e.g. outcomeId -> outcome Id
  const spaced = noUnderscores.replace(/([A-Z])/g, " $1");
  // Capitalize the very first letter
  return spaced.replace(/^./, (m) => m.toUpperCase()).trim();
};

/**
 * Returns the abbreviation for a given timezone.
 *
 * @param {string} timezone - The timezone identifier (e.g., "Europe/Istanbul", "America/Chicago").
 * @returns {string} The abbreviation for the given timezone (e.g., "TRT", "CST" or "CDT").
 *
 */
export const timezoneAbbreviationHelper = (timezone) => {
  // Change to switch statement for more timezone abbreviations
  switch (timezone) {
    case "Europe/Istanbul":
      return "TRT";
    case "America/Sao_Paulo":
      return "BRT";
    default:
      return moment().tz(timezone).format("z");
  }
};

// Convert date string to a formatted date string in the user's local timezone
export const convertDateToLocaleTz = (
  dateString,
  tz = "America/Chicago",
  includeTzName = true
) => {
  try {
    if (!dateString) return dateString;
    const formatted = moment
      .tz(dateString, tz)
      .format(getStoredProgramDateTimeFormat());
    return includeTzName
      ? `${formatted} ${timezoneAbbreviationHelper(tz)}`
      : formatted;
  } catch (e) {
    console.warn(e);
    return dateString;
  }
};

export const convertDateToLocaleTzWithSeconds = (
  dateString,
  tz = "America/Chicago",
  includeTzName = true
) => {
  try {
    if (!dateString) return dateString;
    const formatted = moment
      .tz(dateString, tz)
      .format(ensureDateTimeFormatHasSeconds(getStoredProgramDateTimeFormat()));
    return includeTzName
      ? `${formatted} ${timezoneAbbreviationHelper(tz)}`
      : formatted;
  } catch (e) {
    console.warn(e);
    return dateString;
  }
};

// Convert date string to a specific format in a given timezone (defaults to stored program date format)
export const formatDateTz = (dateString, tz = "America/Chicago") => {
  try {
    if (!dateString) return "-";
    return moment.tz(dateString, tz).format(getStoredProgramDateFormat());
  } catch (e) {
    console.warn(e);
    return dateString;
  }
};

// Capitalize the first letter of a string
export const capitalizeFirstLetter = (string) =>
  string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();

// Capitalize the first letter of each word in a sentence
export const capitalizeFirstLetterOfEachWord = (sentence) => {
  return sentence
    .replace(/_/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

// Format start and end dates with a hyphen separator (uses stored program date format)
export const formatDatesWithHyphen = (start, end, tz = "America/Chicago") => {
  if (!start && !end) return "-";

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return formatDateTz(dateString, tz);
  };

  return `${formatDate(start)} - ${formatDate(end)}`;
};

// Validate email domains in an array of domains
export const validateDomains = (domains) => {
  if (!domains || !domains.length) return false;
  const pattern = "@\\w+(\\.\\w+)+";
  const regEx = new RegExp(pattern);
  const validDomains = domains.every((domain) => regEx.test(domain));
  return validDomains;
};

export const returnPlaceHolderIfEmpty = (actualValue, placeHolder) =>
  actualValue !== null && actualValue !== undefined && actualValue !== ""
    ? actualValue
    : placeHolder;

/**
 * Generates a cryptographically secure random number between 0 (inclusive) and 1 (exclusive).
 * Falls back to Math.random() only if crypto API is unavailable.
 * @returns {number} A random number between 0 and 1
 */
export const secureRandom = () => {
  const cryptoObj = window.crypto || window.msCrypto;
  if (cryptoObj && cryptoObj.getRandomValues) {
    const arr = new Uint32Array(1);
    cryptoObj.getRandomValues(arr);
    return arr[0] / 4294967296;
  }
  return Math.random();
};

export const randomString = (preffix) =>
  `${preffix}_${Date.now() + Math.floor(secureRandom() * 1000)}`;

export const removeEmptyValues = (obj) => {
  return _.omitBy(obj, (value) => {
    if (_.isNumber(value) || _.isBoolean(value)) {
      return false;
    }
    return _.isEmpty(value);
  });
};

export const convertToTitleCase = (string) => {
  if (!string) {
    return "";
  }
  return string.toLowerCase().replace(/\b\w/g, (s) => s.toUpperCase());
};

// Utility function to check if an object is empty
export const isEmptyObject = (obj) =>
  Object.keys(obj).length === 0 && obj.constructor === Object;

/**
 * Returns the TargetId for a "latam-award-sweepstakes-entry' outcome.
 *
 * @param {array} outcomes - The activity outcomes.
 * @returns {string} The TargetId for a "latam-award-sweepstakes-entry' outcome.
 */
export const getTargetId = (outcomes) => {
  const targetObject = outcomes
    .flat()
    .find(
      (obj) =>
        obj?.outcomeDefinition?.slug === "latam-award-sweepstakes-entry" &&
        obj?.outcomeData?.target_id !== null &&
        obj?.outcomeData?.target_id
    );
  return targetObject?.outcomeData?.target_id || null;
};

/**
 * Formats an active date range for display.
 *
 * @param {string} [activeStartDate] - The start date of the active range.
 * @param {string} [activeEndDate] - The end date of the active range.
 * @returns {string} A string representation of the active date range.
 */
export const getActiveDate = (activeStartDate, activeEndDate) => {
  const startDate = activeStartDate ? activeStartDate.replace(/ .*/, "") : "";
  const endDate = activeEndDate ? activeEndDate.replace(/ .*/, "") : "";
  return `${startDate} - ${endDate}`;
};

/**
 * Updates the outcomes by adding the sweepstakes title for the "latam-award-sweepstakes-entry" outcome if the target_id is not null.
 *
 * @param {array} outcomes - The activity outcomes.
 * @param {array} sweepstakesList - The list of sweepstakes.
 * @returns {array} The updated outcomes with the sweepstakes title added to the "latam-award-sweepstakes-entry" outcome.
 */
export const updateSweepstakesOutcomes = (outcomes, sweepstakesList) => {
  let updatedOutcomes = outcomes?.map((outcome) => ({
    ...outcome,
    outcomeData: {
      ...outcome?.outcomeData,
      ...(outcome?.outcomeDefinition.slug == "latam-award-sweepstakes-entry" &&
        outcome?.outcomeData?.target_id != null && {
          sweepstakes:
            sweepstakesList?.find(
              (sweepstake) => sweepstake.id === outcome?.outcomeData?.target_id
            )?.title || "",
        }),
    },
  }));
  return updatedOutcomes;
};
/**
 * Returns the active sweepstakes status as "Not Started" if the start date has not passed, or "Active" if the start date has passed.
 *
 * @param {object} sweepstakes - The sweepstakes.
 * @returns {string} The sweepstakes status.
 */
export const getSweepstakesStatus = (sweepstakes) => {
  const { startsAt, status } = sweepstakes;

  if (status === "NONE") {
    const startDate = new Date(startsAt);
    const sweepstakesStatus = startDate <= new Date() ? "Active" : "Not Started";
    return {
      status: String(sweepstakesStatus).replace(/_/g, " "),
      className: sweepstakesStatus.toLowerCase(),
    };
  } else {
    return {
      status: String(status).replace(/_/g, " "),
      className: status.toLowerCase(),
    };
  }
};

/**
 * Checks if a given value is a valid date.
 *
 * This function attempts to create a `Date` object from the provided value and checks if the resulting date is valid.
 * It returns `true` if the value is a valid date, and `false` otherwise.
 *
 * @param {any} value - The value to be checked.
 * @returns {boolean} `true` if the value is a valid date, `false` otherwise.
 */
export const isDate = (value) => {
  const date = new Date(value);
  return !isNaN(date.getTime());
};

/**
 *  Updates the UI Schema based on the editable fields.
 * @param {object} schema  - UI Schema
 * @param {array} editableFields - Array of editable fields
 * @returns {object} - Updated UI Schema with disabled fields
 */

export function applyEditableFieldRules(schema, editableFields = []) {
  schema = JSON.parse(JSON.stringify(schema));
  function processElement(element) {
    const newElement = { ...element };

    if (newElement.scope) {
      const propertyName = newElement.scope.split("/").pop();
      if (editableFields.length > 0) {
        newElement.disabled = !editableFields.includes(propertyName);
        newElement.enabled = editableFields.includes(propertyName);
        newElement.readOnly = !editableFields.includes(propertyName);
        newElement.readonly = !editableFields.includes(propertyName);
      } else {
        delete newElement.disabled;
        delete newElement.enabled;
        delete newElement.readOnly;
        delete newElement.readonly;
      }
    }

    if (newElement.elements && Array.isArray(newElement.elements)) {
      newElement.elements = newElement.elements.map(processElement);
    }

    return newElement;
  }

  const modifiedSchema = JSON.parse(JSON.stringify(schema));
  if (modifiedSchema.elements && Array.isArray(modifiedSchema.elements)) {
    modifiedSchema.elements = modifiedSchema.elements.map(processElement);
  }

  return modifiedSchema;
}

/**
 * Checks the latam campaign type
 * @param {String} campaignType - The campaign type.
 * @returns {boolean} - True if the campaign is LATAM, false otherwise.
 */
export const isLatamCampaign = (campaignType) => {
  return campaignType?.startsWith("latam_") || campaignType?.includes("_latam");
};

/**
 * Checks If the campaign has the user participation history content,
 * @param {Object} campaign - The campaign object.
 * @returns {boolean} - True if campaign has user participation history content in content configuration, false otherwise.
 */
export const hasUserParticipationHistoryContent = (campaign) => {
  let isUserParticipationHistoryContentAvailable = false;
  const contentData = campaign?.campaignContent?.contentData;
  if (!campaign || !contentData || !contentData?.l10n) {
    return false;
  }
  const languages = Object.keys(contentData.l10n); // Get all configured languages

  languages?.map((lang) => {
    const localeData = contentData.l10n[lang];

    Object.entries(localeData).forEach(([key, value]) => {
      if (
        key === "user_participation_history_widget" &&
        Array.isArray(value) &&
        value.length > 0
      ) {
        isUserParticipationHistoryContentAvailable = true;
      }
    });
    return lang;
  });
  return isUserParticipationHistoryContentAvailable;
};

/**
 * Find the control element in the UISchema corresponding to a JSON Schema property path.
 * @param {object} uischema - The UISchema.
 * @param {string} propertyPath - The property path (e.g., "#/properties/name").
 * @returns {object|null} - The matching control element, or null if not found.
 */
export const findControlInUISchema = (uischema, propertyPath) => {
  if (!uischema || typeof uischema !== "object") {
    return null;
  }
  if (uischema.type === "Control" && uischema.scope === propertyPath) {
    return uischema;
  }
  if (uischema.elements && Array.isArray(uischema.elements)) {
    for (const element of uischema.elements) {
      const result = findControlInUISchema(element, propertyPath);
      if (result) {
        return result;
      }
    }
  }
  return null;
};

export const parseGraphQLError = (error) => {
  const graphQLError = error?.graphQLErrors?.[0];
  const extensionErrors = graphQLError?.extensions?.errors;
  const match = graphQLError?.message?.match(/"detail":"([^"]+)"/);
  const errorMessage =
    (typeof extensionErrors === "string"
      ? extensionErrors
      : extensionErrors?.issues?.[0]?.detail) ||
    (match ? match[1] : graphQLError?.message) ||
    error?.message ||
    "Something Went Wrong";
  return errorMessage;
};

/*
 * Find the control element in the UISchema corresponding to a JSON Schema property path.
 * @param {object} uischema - The UISchema.
 * @param {string} propertyScope - The property path (e.g., "#/properties/name").
 * @param {string} constraintType - The propery name (e.g., "minimum", "title").
 * @param {string} newConstraintValue - New value for property.
 * @returns {object} - The Updated Schema.
 */

export function updatePropertyConstraintInSchema(
  uiSchema,
  propertyScope,
  constraintType,
  newConstraintValue
) {
  const modifiedSchema = JSON.parse(JSON.stringify(uiSchema));

  function loopAndUpdateConstraint(elements) {
    for (let element of elements) {
      if (element.scope === propertyScope) {
        element[constraintType] = newConstraintValue;
      }
      if (element.elements) {
        loopAndUpdateConstraint(element.elements);
      }
    }
  }
  loopAndUpdateConstraint(modifiedSchema.elements);

  return modifiedSchema;
}
/**
 * Checks If the campaign has the user winners widget content,
 * @param {Object} campaign - The campaign object.
 * @returns {boolean} - True if campaign has winners widget content in content configuration, false otherwise.
 */
export const hasWinnersWidgetContent = (campaign) => {
  let isWinnersWidgetContentAvailable = false;
  const contentData = campaign?.campaignContent?.contentData;
  if (!campaign || !contentData || !contentData?.l10n) {
    return false;
  }
  const languages = Object.keys(contentData.l10n); // Get all configured languages

  languages?.map((lang) => {
    const localeData = contentData.l10n[lang];

    Object.entries(localeData).forEach(([key, value]) => {
      if (key === "winners_widget" && Array.isArray(value) && value.length > 0) {
        isWinnersWidgetContentAvailable = true;
      }
    });
    return lang;
  });
  return isWinnersWidgetContentAvailable;
};

/**
 * Checks if the current URL contains the specified path.
 * @param {string} path - The path to check for in the URL.
 * @returns {boolean} - True if the current URL contains the specified path, false otherwise.
 */
export const isPathInCurrentURL = (path) => {
  return window.location.pathname.includes(path);
};

export const checkForSchemas = (jsonschema, uischema) => {
  if (!jsonschema && !uischema) {
    return false; // No schemas available
  }
  if (Object.keys(jsonschema).length === 0 && Object.keys(uischema).length === 0) {
    return false; // Empty schemas
  }
  return true; // Valid schemas present
};

/**
 * Recursively extracts default values from a JSON schema, including nested objects and arrays.
 * @param {object} schema - The JSON schema.
 * @returns {any} - The default value(s) for the schema.
 */
export function getDefaultsFromSchema(schema) {
  if (!schema) return undefined;

  if (schema.default !== undefined) {
    return schema.default;
  }

  if (schema.type === "object" && schema.properties) {
    const obj = {};
    Object.entries(schema.properties).forEach(([key, subschema]) => {
      const def = getDefaultsFromSchema(subschema);
      if (def !== undefined) obj[key] = def;
    });
    return Object.keys(obj).length ? obj : undefined;
  }

  if (schema.type === "array" && schema.items) {
    if (schema.default !== undefined) return schema.default;
    const itemDefault = getDefaultsFromSchema(schema.items);
    return itemDefault !== undefined ? [itemDefault] : undefined;
  }

  return undefined;
}

// Helper function to get translated text with fallback
export const getTranslatedText = (t, key, fallback) => {
  if (!key) return fallback || "";
  const translated = t(key);
  // If translation key doesn't exist, t() returns the key itself
  // In that case, use the fallback value
  return translated === key ? fallback : translated;
};

const PRE_GENERATED_SLUGS = ["upload", "unique-code-group"];
const PRE_GENERATED_NAMES = ["upload", "uploaded", "pre generated", "pre-generated"];
const SYSTEM_GENERATED_NAME = "system";

/**
 * Returns the invalid reason codes for an engagement/performance item, truncated to a max length.
 * @param {object} item - The engagement/performance item.
 * @returns {string} - The invalid reason codes text, truncated with "...", or "-" if none exist.
 */
export const renderInvalidReasonCodes = (item) => {
  const MAX_CHARS = 100;
  const failureReasonCodes = (item?.failureReasons || [])
    .map((failureReason) => failureReason?.code)
    .filter(Boolean);

  if (failureReasonCodes.length === 0) return "-";

  const fullText = failureReasonCodes.join(", ");
  if (fullText.length <= MAX_CHARS) return fullText;

  return `${fullText.slice(0, MAX_CHARS)}...`;
};

/**
 * Returns the code group creationType filter based on the activity slug and name.
 * @param {string} activitySlug - The activity definition slug.
 * @param {string} activityName - The activity definition name.
 * @returns {object} - The creationType filter object or an empty object if no match.
 */
export const getCodeGroupCreationTypeFilter = (activitySlug, activityName) => {
  const normalizedSlug = activitySlug?.toLowerCase() || "";
  const normalizedName = activityName?.toLowerCase() || "";

  const isPreGenerated =
    PRE_GENERATED_SLUGS.some((slug) => normalizedSlug.includes(slug)) ||
    PRE_GENERATED_NAMES.some((name) => normalizedName.includes(name));

  if (isPreGenerated) {
    return { creationType: ["PRE_GENERATED"] };
  }

  const isSystemGenerated =
    normalizedSlug.includes(SYSTEM_GENERATED_NAME) ||
    normalizedName.includes(SYSTEM_GENERATED_NAME);

  if (isSystemGenerated) {
    return { creationType: ["SYSTEM_GENERATED"] };
  }

  return {};
};
